// ---------------------------------------------------------------------------
// The Overview's campaign list inside a project, on the system's DataTable (R15d)
//
// The list the Overview (dashboard.ts) shows inside a project, built as
// ConsoleOverview and the DataTable's README build a campaign table: a flush
// Panel "Campaigns" with the count line and the archived toggle, and in it
// the system's DataTable (design/table.ts, with R10f's column options).
//
//   - Campaign (primary): the name, "Untitled draft" without one (7.5), cut
//     to one line with an ellipsis (README: truncate, never wrap); its
//     second line the channel and its one date, as the README's campaign
//     table writes a row's context: "Email · Launched Oct 14, 2026", "Email
//     + LinkedIn" for a cross-channel campaign. The channel had a column of
//     its own; the README asks for about six columns and keeps context on the
//     second line, so it moved there.
//   - Status, second (README): the system's StatusBadge, which knows every
//     state of the app. Sorted in the README's order of severity: error,
//     paused, ready to launch, cool-down, running or active, generating,
//     scheduled, draft, completed, archived; a state it does not list last.
//   - Leads, Sent, Opted out, Replied, right-aligned, en-US as the tiles
//     write them (formatCount). Sent is leadsContacted (R15c), the count the
//     third tile adds up, "—" when a campaign came without it, and carries
//     the README's one inline bar: the share of the campaign's leads, said to
//     a screen reader after the figure (", 2,980 of 4,812 leads", or ", not
//     counted"). Sorted largest first; no count sorts below every count.
//   - The actions last, under a header only a screen reader reads; the page
//     draws them (dashboard.ts), because they hold its state.
// Sorting is the DataTable's own, from the headers; until a header is
// pressed the rows keep the server's order (by updatedAt). A row opens the
// campaign from a click, or from the keyboard: the DataTable makes each row a
// tab stop that Enter and Space open (onRowClick), which today's rows were not.
//
// Empty, the table says why inside itself, as the system's EmptyState (size
// sm, inside a panel): a project without campaigns is a first run whose one
// action starts the first campaign; a project whose campaigns are all
// archived is a filter that matches nothing, pointing at the toggle above.
// Both keep to the part of the table the panel shows, so a phone sees them
// whole (found in Chrome).
//
// Show archived is a small ghost button with aria-pressed in the Panel's
// head, without an icon: a filter of the list draws none, as R4's icon map
// decided for it (its meaning left the map with its last Material icon). It
// adds the archived campaigns to the list, as before.
// ---------------------------------------------------------------------------

import { html, type TemplateResult } from 'lit';

import { statusBadge } from '../design/badge.js';
import { button } from '../design/button.js';
import '../design/feedback.js';
import { format } from '../design/helpers.js';
import { panel } from '../design/panel.js';
import '../design/table.js';
import type { Column } from '../design/table.js';
import { formatCount, leadsContacted } from './dashboard-figures.js';

/** The fields of a campaign the list reads. */
export interface CampaignRow {
  _id: string;
  name: string;
  campaignType: string;
  status: string;
  leadsProcessed?: number;
  totalSent?: number;
  totalOptedOut?: number;
  totalReplied?: number;
  createdAt: string;
  launchedAt?: string;
  archivedAt?: string;
}

/** What the list and its buttons call a campaign: its name, or what it is without one (7.5). */
export const campaignName = (c: { name?: string }) => c.name || 'Untitled draft';

/** The channel in words; anything but email and cross-channel is LinkedIn, as the list always said. */
export function channelWord(type: string): string {
  return type === 'email' ? 'Email' : type === 'multi' ? 'Email + LinkedIn' : 'LinkedIn';
}

/** The row's one date: archived, else launched, else created. */
export function dateLine(c: Pick<CampaignRow, 'status' | 'archivedAt' | 'launchedAt' | 'createdAt'>): string {
  if (c.status === 'archived' && c.archivedAt) return `Archived ${format.date(c.archivedAt)}`;
  if (c.launchedAt) return `Launched ${format.date(c.launchedAt)}`;
  return `Created ${format.date(c.createdAt)}`;
}

/** The StatusBadge README's order of severity for a sorted status column; a state it does not list comes last. */
const SEVERITY: Record<string, number> = {
  error: 0,
  paused: 1,
  ready_to_launch: 2,
  preparing_to_launch: 3,
  running: 4,
  active: 4,
  generating: 5,
  scheduled: 6,
  draft: 7,
  completed: 8,
  archived: 9,
};
export const statusRank = (status: string) => (Object.hasOwn(SEVERITY, status) ? (SEVERITY[status] as number) : 10);

const count = (n: number | undefined) => formatCount(n ?? 0);

/** The list's columns; `actions` draws a row's buttons. */
export function campaignColumns<C extends CampaignRow>(actions: (c: C) => unknown): Column<C>[] {
  return [
    {
      key: 'name',
      header: 'Campaign',
      primary: true,
      sortable: true,
      sortValue: campaignName,
      // Cut to one line (README: "Don't wrap cell text. Truncate"; found in Chrome): the cell's nowrap kept a long
      // name's whole width, and Opted out, Replied and the actions left the panel at 1280px. Free to break, its
      // narrowest width is a word, so the column takes what the others leave; the ellipsis cuts the one line.
      render: c => html`<span class="line-clamp-1 whitespace-normal wrap-anywhere">${campaignName(c)}</span>`,
      secondary: c => `${channelWord(c.campaignType)} · ${dateLine(c)}`,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortValue: c => statusRank(c.status),
      render: c => statusBadge({ status: c.status }),
    },
    {
      key: 'leadsProcessed',
      header: 'Leads',
      align: 'right',
      sortable: true,
      sortValue: c => c.leadsProcessed ?? 0,
      render: c => count(c.leadsProcessed),
    },
    {
      key: 'totalSent',
      header: 'Sent',
      align: 'right',
      sortable: true,
      // A count that did not come is no 0: below every count, largest first.
      sortValue: c => leadsContacted(c) ?? -1,
      render: c => {
        const sent = leadsContacted(c);
        return sent === undefined ? '—' : formatCount(sent);
      },
      bar: {
        value: c => leadsContacted(c) ?? 0,
        max: c => c.leadsProcessed ?? 0,
        label: c => {
          const sent = leadsContacted(c);
          return sent === undefined ? 'not counted' : `${formatCount(sent)} of ${count(c.leadsProcessed)} leads`;
        },
      },
    },
    {
      key: 'totalOptedOut',
      header: 'Opted out',
      align: 'right',
      sortable: true,
      sortValue: c => c.totalOptedOut ?? 0,
      render: c => count(c.totalOptedOut),
    },
    {
      key: 'totalReplied',
      header: 'Replied',
      align: 'right',
      sortable: true,
      sortValue: c => c.totalReplied ?? 0,
      render: c => count(c.totalReplied),
    },
    { key: 'actions', header: html`<span class="sdr-visually-hidden">Actions</span>`, align: 'right', render: actions },
  ];
}

const plural = (n: number, word: string) => `${formatCount(n)} ${word}${n === 1 ? '' : 's'}`;

export interface CampaignsPanelProps<C extends CampaignRow> {
  /** Every campaign of the project, archived ones too. */
  campaigns: readonly C[];
  showArchived: boolean;
  onToggleArchived: () => void;
  onOpen: (c: C) => void;
  onNewCampaign: () => void;
  actions: (c: C) => unknown;
}

/** The Panel "Campaigns" with its DataTable, its count line, the archived toggle and the empty states. */
export function campaignsPanel<C extends CampaignRow>(p: CampaignsPanelProps<C>): TemplateResult {
  const archived = p.campaigns.filter(c => c.status === 'archived').length;
  // Archived campaigns stay out of the list until asked for: that is what archiving is for.
  const listed = p.showArchived ? p.campaigns : p.campaigns.filter(c => c.status !== 'archived');
  const subtitle =
    p.campaigns.length === 0
      ? undefined
      : `${plural(listed.length, 'campaign')} shown${archived > 0 && !p.showArchived ? ` · ${formatCount(archived)} archived` : ''}`;
  const toggle =
    archived > 0
      ? button(
          { variant: 'ghost', size: 'sm', ariaPressed: p.showArchived, onClick: p.onToggleArchived },
          p.showArchived ? 'Hide archived' : `Show archived (${formatCount(archived)})`,
        )
      : undefined;
  // The empty row spans the table's full width, on a phone the headers' (593px in a 356px panel): centred across
  // it, the state was cut at the panel's edge (found in Chrome). In a box that sticks at the flush cell's padding
  // and is no wider than the panel shows (the page, the @container, less the panel's two 1px borders and the
  // cell's 24px each side), it stays whole wherever the table is scrolled. On a desktop the box is the cell.
  const inView = (state: TemplateResult) =>
    html`<div class="sticky left-6 max-w-[calc(100cqw-3.125rem)]">${state}</div>`;
  const empty = inView(
    p.campaigns.length === 0
      ? html`<sdr-empty-state
          .variant=${'first-run'}
          .size=${'sm'}
          .heading=${'No campaigns in this project yet'}
          .content=${'Start your first one here to begin.'}
          .action=${button({ variant: 'primary', icon: 'plus', onClick: p.onNewCampaign }, 'Start your first campaign')}
        ></sdr-empty-state>`
      : html`<sdr-empty-state
          .variant=${'no-results'}
          .size=${'sm'}
          .heading=${'Every campaign in this project is archived'}
          .content=${'Use “Show archived” above to see them.'}
        ></sdr-empty-state>`,
  );
  return panel(
    { title: 'Campaigns', subtitle, actions: toggle, flush: true },
    html`<sdr-data-table
      .columns=${campaignColumns(p.actions)}
      .rows=${listed}
      .rowKey=${'_id'}
      .tableLabel=${'Campaigns'}
      .onRowClick=${p.onOpen}
      .empty=${empty}
    ></sdr-data-table>`,
  );
}
